import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Alert,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import dayjs from 'dayjs';
import { 
  getSessionWithComparison, 
  compareSession, 
  getPatientBaselineSessions,
  SessionComparison,
  SessionWithComparison 
} from '../../../services/moleMappingApi';

interface BaselineComparisonDialogProps {
  open: boolean;
  onClose: () => void;
  currentSessionId: string;
  patientId: string;
  isFollowupSession?: boolean;
}

const BaselineComparisonDialog: React.FC<BaselineComparisonDialogProps> = ({
  open,
  onClose,
  currentSessionId,
  patientId,
  isFollowupSession = false
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionData, setSessionData] = useState<SessionWithComparison | null>(null);
  const [baselineSessions, setBaselineSessions] = useState<any[]>([]);
  const [selectedBaselineId, setSelectedBaselineId] = useState<string>('');
  const [comparisonNotes, setComparisonNotes] = useState<string>('');

  useEffect(() => {
    if (open) {
      loadSessionData();
      loadBaselineSessions();
    }
  }, [open, currentSessionId]);

  const loadSessionData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await getSessionWithComparison(currentSessionId);
      setSessionData(data);
      
      if (data.session?.baselineSessionId) {
        setSelectedBaselineId(data.session.baselineSessionId);
      }
    } catch (error) {
      console.error('Failed to load session data:', error);
      setError('Failed to load session data');
    } finally {
      setLoading(false);
    }
  };

  const loadBaselineSessions = async () => {
    try {
      const sessions = await getPatientBaselineSessions(patientId);
      setBaselineSessions(sessions);
      
      if (sessions.length > 0 && !selectedBaselineId) {
        setSelectedBaselineId(sessions[0].id);
      }
    } catch (error) {
      console.error('Failed to load baseline sessions:', error);
    }
  };

  const handleCompareToBaseline = async () => {
    if (!selectedBaselineId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      await compareSession(selectedBaselineId, currentSessionId, comparisonNotes);
      await loadSessionData(); // Reload to get updated comparison data
    } catch (error) {
      console.error('Failed to compare sessions:', error);
      setError('Failed to compare sessions');
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'high': return theme.palette.error.main;
      case 'medium': return theme.palette.warning.main;
      case 'low': return theme.palette.success.main;
      default: return theme.palette.grey[500];
    }
  };

  const getRecommendationIcon = (action: string) => {
    switch (action) {
      case 'immediate_referral': return <WarningIcon color="error" />;
      case 'biopsy': return <ScheduleIcon color="warning" />;
      case 'continue_monitoring': return <CheckCircleIcon color="success" />;
      default: return <CheckCircleIcon />;
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '600px'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">
            Baseline Comparison Analysis
          </Typography>
          {sessionData?.session?.sessionType === 'followup' && (
            <Chip 
              label="Follow-up Session" 
              color="primary" 
              size="small" 
            />
          )}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Session Information */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Current Session
                </Typography>
                {sessionData?.session && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Date: {dayjs(sessionData.session.sessionDate).format('MMMM D, YYYY')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Type: {sessionData.session.sessionType || 'baseline'}
                    </Typography>
                    {sessionData.session.intervalMonths && (
                      <Typography variant="body2" color="text.secondary">
                        Interval: {sessionData.session.intervalMonths} months since baseline
                      </Typography>
                    )}
                    {sessionData.session.notes && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Notes: {sessionData.session.notes}
                      </Typography>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Baseline Selection */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Baseline Session
                </Typography>
                
                {!isFollowupSession && (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Select Baseline</InputLabel>
                    <Select
                      value={selectedBaselineId}
                      onChange={(e) => setSelectedBaselineId(e.target.value)}
                      label="Select Baseline"
                    >
                      {baselineSessions.map((session) => (
                        <MenuItem key={session.id} value={session.id}>
                          {dayjs(session.sessionDate).format('MMMM D, YYYY')}
                          {session.notes && ` - ${session.notes.substring(0, 30)}...`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {sessionData?.baselineSession && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Date: {dayjs(sessionData.baselineSession.sessionDate).format('MMMM D, YYYY')}
                    </Typography>
                    {sessionData.baselineSession.notes && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Notes: {sessionData.baselineSession.notes}
                      </Typography>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Comparison Results */}
          {sessionData?.comparison && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Comparison Analysis
                  </Typography>
                  
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="primary">
                          {sessionData.comparison.totalMoles}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Moles
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <AddIcon color="success" />
                          <Typography variant="h4" color="success.main">
                            {sessionData.comparison.newMoles}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          New Moles
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <TrendingUpIcon color="warning" />
                          <Typography variant="h4" color="warning.main">
                            {sessionData.comparison.changedMoles}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Changed Moles
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <RemoveIcon color="error" />
                          <Typography variant="h4" color="error.main">
                            {sessionData.comparison.disappearedMoles}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Disappeared
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  {/* Risk Assessment */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Risk Assessment
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Chip
                        label={`${sessionData.comparison.overallRiskAssessment.toUpperCase()} RISK`}
                        sx={{
                          backgroundColor: alpha(getRiskColor(sessionData.comparison.overallRiskAssessment), 0.1),
                          color: getRiskColor(sessionData.comparison.overallRiskAssessment),
                          fontWeight: 'bold'
                        }}
                      />
                      
                      {sessionData.comparison.highRiskChanges > 0 && (
                        <Chip
                          icon={<WarningIcon />}
                          label={`${sessionData.comparison.highRiskChanges} High-Risk Changes`}
                          color="error"
                          variant="outlined"
                        />
                      )}
                    </Box>

                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          {getRecommendationIcon(sessionData.comparison.recommendedAction)}
                        </ListItemIcon>
                        <ListItemText
                          primary="Recommended Action"
                          secondary={sessionData.comparison.recommendedAction.replace('_', ' ').toUpperCase()}
                        />
                      </ListItem>
                    </List>
                  </Box>

                  {sessionData.comparison.comparisonNotes && (
                    <Box>
                      <Typography variant="subtitle1" gutterBottom>
                        Comparison Notes
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {sessionData.comparison.comparisonNotes}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Comparison Input */}
          {!sessionData?.comparison && selectedBaselineId && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Generate Comparison
                  </Typography>
                  
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Comparison Notes (Optional)"
                    value={comparisonNotes}
                    onChange={(e) => setComparisonNotes(e.target.value)}
                    placeholder="Enter any specific observations or notes about the comparison..."
                    sx={{ mb: 2 }}
                  />
                  
                  <Button
                    variant="contained"
                    onClick={handleCompareToBaseline}
                    disabled={loading || !selectedBaselineId}
                    fullWidth
                  >
                    {loading ? 'Generating Comparison...' : 'Compare to Baseline'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BaselineComparisonDialog;
